import React from 'react';
import {
  Breadcrumbs as MUIBreadcrumbs,
  Link,
  Typography,
  Box,
} from '@mui/material';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

/**
 * Breadcrumb item interface
 */
export interface Breadcrumb {
  label: string;
  path?: string; // undefined = current page (not clickable)
  icon?: React.ReactNode;
}

/**
 * Breadcrumbs Component
 *
 * Displays breadcrumb navigation trail
 *
 * Features:
 * - Clickable links for parent pages
 * - Current page not clickable
 * - Optional icons for breadcrumb items
 * - Responsive text truncation
 *
 * @component
 * @param {BreadcrumbsProps} props - Component props
 * @returns {JSX.Element} Breadcrumb navigation
 */
interface BreadcrumbsProps {
  breadcrumbs: Breadcrumb[];
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ breadcrumbs }) => {
  // ===== Render =====

  if (!breadcrumbs || breadcrumbs.length === 0) {
    return null;
  }

  return (
    <MUIBreadcrumbs
      separator={<NavigateNextIcon fontSize="small" />}
      aria-label="breadcrumb"
      sx={{
        '& .MuiBreadcrumbs-separator': {
          mx: 0.5,
        },
      }}
    >
      {breadcrumbs.map((crumb, index) => {
        const isLast = index === breadcrumbs.length - 1;

        // Current page (not clickable)
        if (isLast || !crumb.path) {
          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              {crumb.icon}
              <Typography
                color="text.primary"
                sx={{
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  maxWidth: { xs: 150, sm: 250 },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {crumb.label}
              </Typography>
            </Box>
          );
        }

        // Clickable link
        return (
          <Link
            key={index}
            component={RouterLink}
            to={crumb.path}
            underline="hover"
            color="text.secondary"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: '0.875rem',
              '&:hover': {
                color: 'primary.main',
              },
              transition: 'color 0.2s',
            }}
          >
            {crumb.icon}
            <span
              style={{
                maxWidth: 150,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-block',
              }}
            >
              {crumb.label}
            </span>
          </Link>
        );
      })}
    </MUIBreadcrumbs>
  );
};
